/**
 * Bundle 94 - full Dostination Privacy Policy (replaces the
 * placeholder shipped in Bundle 75). Content is authored to the
 * user's 16-section spec: who we are, what we collect, how we use
 * it, third parties, introductions consent, survey/testimonial
 * model, retention, rights, children, security, cookies, transfers,
 * law enforcement, changes notice, contact, effective date.
 *
 * No em/en dashes. No `photo`/`image` in HTML alt (the icon on
 * this page is a Shield lucide-react component).
 */
import React from 'react';
import { Shield } from 'lucide-react';
import { Link } from 'react-router-dom';


export const PRIVACY_POLICY_VERSION = '2026-08-25';


const Section = ({ id, title, children }) => (
  <section
    id={id}
    data-testid={`privacy-section-${id}`}
    className="mt-10"
  >
    <h2 className="font-display text-2xl font-semibold text-primary-900 mb-3">
      {title}
    </h2>
    <div className="space-y-3 text-primary-700 leading-relaxed">
      {children}
    </div>
  </section>
);


const PrivacyPage = () => (
  <div className="min-h-screen bg-white" data-testid="privacy-page">
    <div className="border-b border-neutral-100 bg-neutral-50">
      <div className="container mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center gap-3 text-secondary-800 text-xs uppercase tracking-widest font-medium">
          <Shield className="h-4 w-4" aria-hidden="true" /> Privacy
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-primary-900 mt-3">
          Privacy Policy
        </h1>
        <p className="text-primary-700 mt-3 max-w-2xl">
          Plain English. No selling of your data. This policy explains
          what Dostination collects, why we collect it, and how you can
          exercise your rights over it.
        </p>
        <div className="mt-3 text-xs text-neutral-500" data-testid="privacy-effective-date">
          Effective and last updated: {PRIVACY_POLICY_VERSION}
        </div>
      </div>
    </div>

    <div className="container mx-auto px-4 md:px-8 py-10 max-w-3xl">
      <Section id="who-we-are" title="1. Who we are">
        <p>
          Dostination is a curated events platform for intentional, in-person connection experiences. This is not a dating or matchmaking product.
          References to &ldquo;Dostination,&rdquo; &ldquo;we,&rdquo; or &ldquo;us&rdquo;
          in this policy refer to Dostination (the operating entity behind
          <a href="https://dostination.com" className="underline text-secondary-700 ml-1">dostination.com</a>).
          If you have any question about this policy, write to&nbsp;
          <a href="mailto:privacy@dostination.com" className="underline text-secondary-700">privacy@dostination.com</a>.
        </p>
      </Section>

      <Section id="what-we-collect" title="2. What data we collect">
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Account</strong>: first and last name, email address, phone number, password hash (never the plain password).</li>
          <li><strong>Profile</strong>: profile pictures, short bio, social handles (for example Instagram), preferences, interests, activities.</li>
          <li><strong>Event data</strong>: RSVP details, check-in status, survey responses, testimonial content (only if you grant permission to publish it).</li>
          <li><strong>Connection data</strong>: direct-exchange reports you submit, introduction requests you make, and the mutual-consent records that gate every actual introduction.</li>
          <li><strong>Payment metadata via Stripe</strong>: order status, receipt IDs, and the last four digits of a card. Dostination does NOT store full card numbers or CVC codes.</li>
          <li><strong>Analytics</strong>: page views, referrers, UTM parameters, device and browser metadata (screen size, coarse locale).</li>
          <li><strong>Communications</strong>: emails sent to you, plus opened/clicked events emitted by our email provider.</li>
        </ul>
      </Section>

      <Section id="how-we-use-it" title="3. How we use it">
        <ul className="list-disc pl-5 space-y-1">
          <li>Operate events (RSVP, check-in, seating, event introductions).</li>
          <li>Send transactional emails you asked for (confirmations, receipts, event reminders).</li>
          <li>Send occasional marketing emails you can opt out of at any time from any email or by writing to <a className="underline text-secondary-700" href="mailto:privacy@dostination.com">privacy@dostination.com</a>.</li>
          <li>Analyze aggregate product usage to make Dostination better.</li>
          <li>Prevent fraud and abuse.</li>
          <li>Comply with law when we are required to.</li>
        </ul>
      </Section>

      <Section id="third-parties" title="4. Third parties we share data with">
        <p>We work with a small set of specialised vendors. Each one processes only the minimum data needed for its job.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Stripe</strong>: payment processing. Card details go directly from your browser to Stripe.</li>
          <li><strong>Resend</strong>: transactional email delivery + open/click webhooks.</li>
          <li><strong>Meta Conversions API</strong>: advertising attribution for our own campaigns.</li>
          <li><strong>PostHog</strong>: product analytics. PostHog is DISABLED on <code>/survey/*</code> and <code>/connection-followups/*</code> and on the introduction-consent pages so private reflection stays private.</li>
          <li><strong>MaxMind GeoLite2</strong>: IP-based city-level geolocation used for fraud and event-region features.</li>
          <li><strong>OpenAI via Emergent LLM</strong>: powers AI-assisted features such as seating narratives and gentle nudge generation. We never send your survey freeform text to any LLM.</li>
          <li><strong>Google Identity</strong>: if you sign in with Google, Google provides us your name and email.</li>
        </ul>
      </Section>

      <Section id="introductions" title="5. Introductions and consent">
        <p>
          Dostination never shares anyone&apos;s contact details without their explicit consent.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>When you request an introduction, a Dostination admin first identifies and speaks privately with the other person.</li>
          <li>An introduction only happens if BOTH people agree.</li>
          <li>Either party can decline at any time. We do NOT tell the requester the reason.</li>
          <li>You can withdraw a request or an active consent at any time from your profile or by emailing <a className="underline text-secondary-700" href="mailto:privacy@dostination.com">privacy@dostination.com</a>.</li>
        </ul>
      </Section>

      <Section id="surveys" title="6. Survey data and testimonials">
        <p>
          Post-event surveys are private by default. The freeform &ldquo;What
          would you tell someone considering a future Dostination event?&rdquo;
          field is saved as private feedback UNLESS you explicitly check the
          permission box on the same step.
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>QA / test rows (flagged <code>is_test</code>) are excluded from ratings, connection analytics, discovery-source analytics, testimonials, response-rate counters, and success-story follow-ups.</li>
          <li>If you grant testimonial permission, we may share your feedback under Initials or First Name (your choice; default is Initials). No profile picture is ever attached.</li>
          <li>Withdrawing consent takes it down within seven days across the website, event pages, and any social share cards we generated.</li>
        </ul>
      </Section>

      {/* Bundle 144 - Event Photography & Video section. Data-testid
          used by both the a11y baseline and the Bundle 144
          regression suite. Anchor to the matching Terms clause via
          /terms#event-photography-video. */}
      <section
        id="event-photography-video"
        data-testid="privacy-clause-event-photography-video"
        className="mt-10"
      >
        <h2 className="font-display text-2xl font-semibold text-primary-900 mb-3">
          Event Photography &amp; Video
        </h2>
        <div className="space-y-3 text-primary-700 leading-relaxed">
          <p>
            We may capture photographs and video at Dostination events to
            document the event experience and create event recaps, social
            media content, and promotional materials. Our media team
            generally focuses on the atmosphere, activities, and group
            moments rather than private conversations. Please see our{' '}
            <Link
              to="/terms#event-photography-video"
              className="underline text-secondary-700"
              data-testid="privacy-event-photography-video-terms-link"
            >
              Event Terms &amp; Conditions
            </Link>
            {' '}for additional information about photography and video at
            Dostination events.
          </p>
        </div>
      </section>

      <Section id="retention" title="7. Data retention">
        <ul className="list-disc pl-5 space-y-1">
          <li>Active account data is retained for as long as your account is open.</li>
          <li>If you request deletion, account and profile data is deleted within 30 days of the request. Some records must be retained for tax, fraud, and legal reasons (Stripe receipts, dispute history) for the periods those laws require.</li>
          <li>Event data (RSVPs, check-ins, survey responses) is retained for up to 3 years for cohort analysis and legal record-keeping.</li>
          <li>Aggregate anonymised analytics may be retained indefinitely because it no longer identifies you.</li>
        </ul>
      </Section>

      <Section id="your-rights" title="8. Your rights">
        <p>You can:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Access the data we hold about you.</li>
          <li>Ask us to correct anything that is wrong.</li>
          <li>Ask us to delete your account.</li>
          <li>Port your data to a competing service.</li>
          <li>Opt out of marketing emails.</li>
          <li>Withdraw any consent (introductions, testimonials, marketing) at any time.</li>
        </ul>
        <p>
          Send requests to <a className="underline text-secondary-700" href="mailto:privacy@dostination.com">privacy@dostination.com</a>. We reply within 30 days.
        </p>
      </Section>

      <Section id="children" title="9. Children">
        <p>Dostination is for adults 18 and older. We do not knowingly collect data from anyone under 18.</p>
      </Section>

      <Section id="security" title="10. Security">
        <ul className="list-disc pl-5 space-y-1">
          <li>Everything is encrypted in transit via TLS 1.2 or better.</li>
          <li>Passwords are stored as bcrypt hashes; we never see or store the plaintext.</li>
          <li>Card details never touch our servers. Stripe handles PCI compliance.</li>
          <li>Admin access is restricted by role, audited, and MFA-encouraged.</li>
        </ul>
      </Section>

      <Section id="cookies" title="11. Cookies and tracking">
        <ul className="list-disc pl-5 space-y-1">
          <li>We set a first-party session cookie so you stay signed in.</li>
          <li>PostHog sets a first-party analytics cookie on general pages. You can opt out from your profile at any time; opting out does not affect service.</li>
          <li>Meta Pixel loads on general marketing pages for advertising attribution and respects Do-Not-Track settings.</li>
          <li>On <code>/survey/*</code>, <code>/connection-followups/*</code>, and the introduction-consent pages, ALL third-party tracking is disabled. Those routes also carry <code>X-Robots-Tag: noindex, nofollow</code>, <code>Referrer-Policy: no-referrer</code>, and <code>Cache-Control: no-store</code> so no downstream service can capture the URL or contents.</li>
        </ul>
      </Section>

      <Section id="international-transfers" title="12. International transfers">
        <p>
          Your data may be processed in the United States by our vendors (Stripe, Resend, PostHog, OpenAI, MaxMind). Where required by law, transfers are covered by Standard Contractual Clauses.
        </p>
      </Section>

      <Section id="law-enforcement" title="13. Law enforcement">
        <p>
          We respond to lawful requests from law enforcement. See our&nbsp;
          <Link to="/law-enforcement-guidelines" className="underline text-secondary-700" data-testid="privacy-le-link">Law Enforcement Guidelines</Link>
          &nbsp;for the process, contact information, and the standards we hold requests to.
        </p>
      </Section>

      <Section id="changes" title="14. Changes to this policy">
        <p>
          If we make a material change, we will email every registered user at least 30 days before the change takes effect. Minor edits (typos, clarifying language) may happen without notice; the effective date at the top of this page always reflects the latest version.
        </p>
      </Section>

      <Section id="contact" title="15. Contact">
        <p>
          Privacy questions and data-rights requests: <a className="underline text-secondary-700" href="mailto:privacy@dostination.com">privacy@dostination.com</a>.
        </p>
        <p className="text-xs text-neutral-500" data-testid="privacy-mailing-address">
          Mailing address: [MAILING_ADDRESS_TBD]. We will publish a full mailing address once our operating entity is finalised. Until then, email is the fastest way to reach us.
        </p>
      </Section>

      <Section id="effective-date" title="16. Effective date">
        <p>This policy is effective as of <strong>{PRIVACY_POLICY_VERSION}</strong>.</p>
        <p className="text-sm">
          See also our&nbsp;
          <Link to="/terms" className="underline text-secondary-700" data-testid="privacy-terms-link">Terms and Conditions</Link>
          &nbsp;and our&nbsp;
          <Link to="/law-enforcement-guidelines" className="underline text-secondary-700">Law Enforcement Guidelines</Link>.
        </p>
      </Section>
    </div>
  </div>
);


export default PrivacyPage;
